import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuthStore } from '../auth/auth.store';

/**
 * The single call the shell makes on mount.
 *
 * Returns the society's branding, its plan features, the runtime flags and the
 * counts that drive sidebar badges — so the first paint already knows what to
 * show and what to hide, rather than flashing a full menu and then removing
 * half of it.
 */

export interface BootstrapData {
  society: {
    id: string;
    name: string;
    slug: string;
    /** The dues currency: what bills and payments in this society are in. */
    currency: string;
    logoUrl: string | null;
    logoUrlDark: string | null;
    faviconUrl: string | null;
    faviconUrlDark: string | null;
    brandDisplay: 'both' | 'logo' | 'name' | null;
    logoHeight: number | null;
    brandColor: string;
    status: string;
    defaultLocale: string;
    timezone: string;
  };
  features: Record<string, boolean>;
  limits: Record<string, number>;
  flags: Record<string, boolean>;
  /** Every platform feature flag, evaluated for this society. */
  earlyAccess?: { key: string; name: string; description: string | null; enabled: boolean }[];
  subscription: {
    status: string | null;
    planCode: string | null;
    planTier: string | null;
    isReadOnly: boolean;
    trialDaysLeft?: number | null;
    /** No trial and nothing bought yet: the console's first job is to sell a plan. */
    needsPlan?: boolean;
  };
  roles: string[];
  permissions: string[];
}

interface AdminDashboardCounts {
  actionItems: {
    pendingApprovals: number;
    slaBreachedComplaints: number;
    pendingBookings: number;
    pendingMoves: number;
    activeSos: number;
  };
  complaints: { open: number };
  subscription: { trialDaysLeft: number | null };
}

export interface SidebarCounts {
  pendingApprovals: number;
  openComplaints: number;
  pendingBookings: number;
  unreadNotifications: number;
}

export function useBootstrap(): {
  bootstrap: (BootstrapData & { subscription: BootstrapData['subscription'] }) | undefined;
  counts: SidebarCounts;
  isLoading: boolean;
} {
  const isAuthenticated = useAuthStore((s) => Boolean(s.accessToken && s.activeSocietyId));

  const bootstrapQuery = useQuery({
    queryKey: ['bootstrap'],
    queryFn: () => api.get<BootstrapData>('/api/v1/dashboard/bootstrap'),
    enabled: isAuthenticated,
    // Plan and flag changes take effect within a minute; refetching more often
    // buys nothing and costs a request on every navigation.
    staleTime: 60_000,
    retry: 1,
  });

  const countsQuery = useQuery({
    queryKey: ['sidebar-counts'],
    queryFn: () => api.get<AdminDashboardCounts>('/api/v1/dashboard/admin'),
    enabled: isAuthenticated,
    staleTime: 30_000,
    // The badges are ambient, not critical; a failure should not surface an
    // error boundary over the whole shell.
    retry: false,
  });

  const unreadQuery = useQuery({
    queryKey: ['unread-count'],
    queryFn: () => api.get<{ count: number }>('/api/v1/notifications/unread-count'),
    enabled: isAuthenticated,
    staleTime: 30_000,
    retry: false,
  });

  const bootstrap = bootstrapQuery.data
    ? {
        ...bootstrapQuery.data,
        subscription: {
          ...bootstrapQuery.data.subscription,
          trialDaysLeft: countsQuery.data?.subscription.trialDaysLeft ?? null,
        },
      }
    : undefined;

  return {
    bootstrap,
    counts: {
      pendingApprovals: countsQuery.data?.actionItems.pendingApprovals ?? 0,
      openComplaints: countsQuery.data?.complaints.open ?? 0,
      pendingBookings: countsQuery.data?.actionItems.pendingBookings ?? 0,
      unreadNotifications: unreadQuery.data?.count ?? 0,
    },
    isLoading: bootstrapQuery.isLoading,
  };
}

/** Is a plan feature available to this society? */
export function useFeature(key: string): boolean {
  const { bootstrap } = useBootstrap();
  // Default to true while loading, so a slow bootstrap does not flash the
  // upgrade prompt at a society that already pays for the feature.
  return bootstrap?.features[key] ?? true;
}

/** Is a runtime flag on for this society? */
export function useFlag(key: string): boolean {
  const { bootstrap } = useBootstrap();
  return bootstrap?.flags[key] ?? false;
}
